/**
 * i18next setup for the browser.
 *
 * Detection chain (matches the API's locale-middleware so a logged-in user
 * sees the same language client- and server-side):
 *   1. ?lang= query string
 *   2. preferred_lang cookie
 *   3. localStorage (i18nextLng)
 *   4. Accept-Language navigator header
 *   5. DEFAULT_LOCALE fallback ('en')
 *
 * Locale files are imported statically — Vite bundles them and the i18n
 * instance is hydrated synchronously at boot, so there is no flash of
 * untranslated content.
 */
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import {
  DEFAULT_LOCALE,
  NAMESPACES,
  SUPPORTED_LOCALES,
  getDir,
  isSupportedLocale,
  type Locale,
} from '@clari/i18n'

import enCommon from '@clari/i18n/locales/en/common.json'
import enAuth from '@clari/i18n/locales/en/auth.json'
import enErrors from '@clari/i18n/locales/en/errors.json'
import enDashboard from '@clari/i18n/locales/en/dashboard.json'

import frCommon from '@clari/i18n/locales/fr/common.json'
import frAuth from '@clari/i18n/locales/fr/auth.json'
import frErrors from '@clari/i18n/locales/fr/errors.json'
import frDashboard from '@clari/i18n/locales/fr/dashboard.json'

import arCommon from '@clari/i18n/locales/ar/common.json'
import arAuth from '@clari/i18n/locales/ar/auth.json'
import arErrors from '@clari/i18n/locales/ar/errors.json'
import arDashboard from '@clari/i18n/locales/ar/dashboard.json'

import esCommon from '@clari/i18n/locales/es/common.json'
import esAuth from '@clari/i18n/locales/es/auth.json'
import esErrors from '@clari/i18n/locales/es/errors.json'
import esDashboard from '@clari/i18n/locales/es/dashboard.json'

import deCommon from '@clari/i18n/locales/de/common.json'
import deAuth from '@clari/i18n/locales/de/auth.json'
import deErrors from '@clari/i18n/locales/de/errors.json'
import deDashboard from '@clari/i18n/locales/de/dashboard.json'

import itCommon from '@clari/i18n/locales/it/common.json'
import itAuth from '@clari/i18n/locales/it/auth.json'
import itErrors from '@clari/i18n/locales/it/errors.json'
import itDashboard from '@clari/i18n/locales/it/dashboard.json'

import ptCommon from '@clari/i18n/locales/pt/common.json'
import ptAuth from '@clari/i18n/locales/pt/auth.json'
import ptErrors from '@clari/i18n/locales/pt/errors.json'
import ptDashboard from '@clari/i18n/locales/pt/dashboard.json'

import nlCommon from '@clari/i18n/locales/nl/common.json'
import nlAuth from '@clari/i18n/locales/nl/auth.json'
import nlErrors from '@clari/i18n/locales/nl/errors.json'
import nlDashboard from '@clari/i18n/locales/nl/dashboard.json'

import plCommon from '@clari/i18n/locales/pl/common.json'
import plAuth from '@clari/i18n/locales/pl/auth.json'
import plErrors from '@clari/i18n/locales/pl/errors.json'
import plDashboard from '@clari/i18n/locales/pl/dashboard.json'

import enBanking from '@clari/i18n/locales/en/banking.json'
import frBanking from '@clari/i18n/locales/fr/banking.json'
import arBanking from '@clari/i18n/locales/ar/banking.json'
import esBanking from '@clari/i18n/locales/es/banking.json'
import deBanking from '@clari/i18n/locales/de/banking.json'
import itBanking from '@clari/i18n/locales/it/banking.json'
import ptBanking from '@clari/i18n/locales/pt/banking.json'
import nlBanking from '@clari/i18n/locales/nl/banking.json'
import plBanking from '@clari/i18n/locales/pl/banking.json'

import enBusinessModel from '@clari/i18n/locales/en/business_model.json'
import frBusinessModel from '@clari/i18n/locales/fr/business_model.json'
import arBusinessModel from '@clari/i18n/locales/ar/business_model.json'
import esBusinessModel from '@clari/i18n/locales/es/business_model.json'
import deBusinessModel from '@clari/i18n/locales/de/business_model.json'
import itBusinessModel from '@clari/i18n/locales/it/business_model.json'
import ptBusinessModel from '@clari/i18n/locales/pt/business_model.json'
import nlBusinessModel from '@clari/i18n/locales/nl/business_model.json'
import plBusinessModel from '@clari/i18n/locales/pl/business_model.json'

const resources = {
  en: { common: enCommon, auth: enAuth, errors: enErrors, dashboard: enDashboard, banking: enBanking, business_model: enBusinessModel },
  fr: { common: frCommon, auth: frAuth, errors: frErrors, dashboard: frDashboard, banking: frBanking, business_model: frBusinessModel },
  ar: { common: arCommon, auth: arAuth, errors: arErrors, dashboard: arDashboard, banking: arBanking, business_model: arBusinessModel },
  es: { common: esCommon, auth: esAuth, errors: esErrors, dashboard: esDashboard, banking: esBanking, business_model: esBusinessModel },
  de: { common: deCommon, auth: deAuth, errors: deErrors, dashboard: deDashboard, banking: deBanking, business_model: deBusinessModel },
  it: { common: itCommon, auth: itAuth, errors: itErrors, dashboard: itDashboard, banking: itBanking, business_model: itBusinessModel },
  pt: { common: ptCommon, auth: ptAuth, errors: ptErrors, dashboard: ptDashboard, banking: ptBanking, business_model: ptBusinessModel },
  nl: { common: nlCommon, auth: nlAuth, errors: nlErrors, dashboard: nlDashboard, banking: nlBanking, business_model: nlBusinessModel },
  pl: { common: plCommon, auth: plAuth, errors: plErrors, dashboard: plDashboard, banking: plBanking, business_model: plBusinessModel },
} as const

export const PREFERRED_LANG_COOKIE = 'preferred_lang'

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupCookie: PREFERRED_LANG_COOKIE,
      lookupLocalStorage: 'i18nextLng',
      caches: ['cookie', 'localStorage'],
      cookieMinutes: 60 * 24 * 365, // 1 year
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    returnNull: false,
  })

function applyDirAttribute(locale: string): void {
  const safe: Locale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE
  document.documentElement.setAttribute('lang', safe)
  document.documentElement.setAttribute('dir', getDir(safe))
}

applyDirAttribute(i18n.language)
i18n.on('languageChanged', applyDirAttribute)

export { i18n }
